import { Avatar, AvatarGroup, Box, Button, Flex, FlexProps, forwardRef, HTMLChakraProps, Text } from '@chakra-ui/react';
import NextImage from 'next/image';
import { Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LectureCardTitle } from '@/components/ui/shared/lecture/LectureCard/LectureCardTitle/LectureCardTitle';
import { ProfilePopupNew } from '@/components/ui/shared/profile/ProfilePopup/ProfilePopupNew';
import { ThinProfile } from '@/resources/ThinProfile';

export interface ILectureCardProps extends HTMLChakraProps<'div'> {
  image?: ReactNode;
  tag?: ReactNode;
  icon?: ReactNode;
  indicator?: ReactNode;
  heldAt?: string;
  bodyProps?: FlexProps;
  eventName?: string;
  name: string;
  authors?: Array<ThinProfile>;
  gatherTownUrl?: string;
  displayTime?: boolean;
  profilesEnabled?: boolean;
}

export const LectureCard = forwardRef<ILectureCardProps, 'div'>((props, ref) => {
  const [openProfilePopup, setOpenProfilePopup] = useState(false);

  const {
    image,
    icon,
    tag,
    name,
    authors,
    indicator,
    heldAt,
    eventName,
    bodyProps,
    gatherTownUrl,
    displayTime,
    profilesEnabled = true,
    ...rest
  } = props;
  const { t } = useTranslation();

  function handleOpenPopup(e: React.MouseEvent) {
    if (!profilesEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setOpenProfilePopup(true);
  }

  return (
    <Flex flexDir="column" h="full" _hover={{ textDecor: 'none' }} ref={ref} {...rest}>
      {image}
      <Flex pos="relative" flexDir="column" flexGrow={1} p="4" backgroundColor="white" {...bodyProps}>
        <Flex justifyContent="space-between">
          <Box>{tag}</Box>
          <Box>{icon}</Box>
        </Flex>
        <LectureCardTitle mb="auto" noOfLines={3}>
          {name}
        </LectureCardTitle>
        {eventName && (
          <Text mt={2} textTransform="uppercase" fontSize="xs" lineHeight="shorter" color="gray.700">
            {eventName}
          </Text>
        )}
        {indicator}
        {profilesEnabled && (
          <ProfilePopupNew isOpen={openProfilePopup} onClose={() => setOpenProfilePopup(false)} profiles={authors} />
        )}
        <Button variant="unstyled" fontWeight="normal" as="button" onClick={handleOpenPopup} mt="4">
          <AvatarGroup max={3} spacing="-1" size="xs" mb="2">
            {authors?.map(({ fullName, squareAvatar }) => (
              <Fragment key={fullName + squareAvatar}>
                {!squareAvatar && (
                  <Avatar backgroundColor="violet.500" color="white" name={fullName} src={squareAvatar} />
                )}
                {squareAvatar && (
                  <NextImage
                    width={24}
                    height={24}
                    style={{ borderRadius: '50%', width: '24px', height: '24px' }}
                    src={squareAvatar}
                    alt={fullName}
                  />
                )}
              </Fragment>
            ))}
          </AvatarGroup>
          <Text
            noOfLines={1}
            whiteSpace="break-spaces"
            textOverflow="ellipsis"
            overflow="hidden"
            textAlign="left"
            _hover={{ textDecoration: profilesEnabled ? 'underline' : 'auto' }}
            maxW="250px"
            as="span"
            color="gray.600"
            fontSize="sm"
            lineHeight="base"
          >
            {authors?.[0]?.fullName}
            {authors?.length > 1 && (
              <Fragment> &nbsp;{t('library.lecture.authors', { count: authors.length - 1 })}</Fragment>
            )}
          </Text>
        </Button>
        {gatherTownUrl && (
          <Button
            as="a"
            my={4}
            href={gatherTownUrl}
            target="_blank"
            size="sm"
            w="192px"
            display="flex"
            justifyContent="space-between"
            colorScheme="black"
          >
            {t('gatherTown.buttonLabel')}
          </Button>
        )}
        {displayTime && heldAt && (
          <Button
            backgroundColor="gray.50"
            padding="8px, 16px, 8px, 16px"
            border={'1px solid #E3E5E5'}
            _hover={{
              backgroundColor: 'gray.50',
            }}
            height={9}
            color="gray.800"
            fontSize={'14px'}
            borderRadius="8px"
            my={4}
          >
            START TIME:
            <Text
              as="span"
              fontWeight={400}
              fontFamily="IBM Plex Sans"
              fontSize="14px"
              marginTop="2px"
              marginLeft="4px"
            >
              {heldAt}
            </Text>
          </Button>
        )}
      </Flex>
    </Flex>
  );
});
